import { Injectable, inject } from '@angular/core';
import { explainedInPlace, rethrowAsRefusal } from '@core/helpers/installation-refusal.helper';
import { utcIso } from '@core/helpers/slot-series.helper';
import {
  AcknowledgeFinding,
  MigrationFinding,
  MigrationFindingPage,
  MigrationReportFilter,
  MigrationReportSummary,
} from '@core/models/migration-report.model';
import { ApiService } from '@core/services/api.service';
import { Observable, catchError, map } from 'rxjs';

const ROUTE = '/installations/migration-report';

// The API serialises its SQL datetimes without an offset, and they are UTC.
function parsed(finding: MigrationFinding): MigrationFinding {
  return {
    ...finding,
    installationOid: finding.installationOid ?? null,
    resolvedAt: finding.resolvedAt ? utcIso(finding.resolvedAt) : null,
    firstSeenAt: utcIso(finding.firstSeenAt),
    lastSeenAt: utcIso(finding.lastSeenAt),
  };
}

// Every request fails with an InstallationRefusal.
@Injectable({
  providedIn: 'root',
})
export class MigrationReportService {
  private readonly apiService = inject(ApiService);

  summary(): Observable<MigrationReportSummary> {
    return this.apiService
      .getRaw<MigrationReportSummary>(`${ROUTE}/summary`, {}, explainedInPlace())
      .pipe(catchError(rethrowAsRefusal));
  }

  list(filter: MigrationReportFilter): Observable<MigrationFindingPage> {
    const { findingClass, subjectType, resolution, partnerOid, limit, offset } = filter;
    return this.apiService
      .getRaw<MigrationFindingPage>(
        ROUTE,
        { class: findingClass, subjectType, resolution, partnerOid, limit, offset },
        explainedInPlace(),
      )
      .pipe(
        map(page => ({ ...page, findings: page.findings.map(parsed) })),
        catchError(rethrowAsRefusal),
      );
  }

  // There is no way back from an acknowledge through the API.
  acknowledge(findingOid: string, note: string): Observable<MigrationFinding> {
    const body: AcknowledgeFinding = { resolution: 'acknowledged', note };
    return this.apiService
      .patchRaw<MigrationFinding>(`${ROUTE}/${findingOid}`, body, explainedInPlace())
      .pipe(map(parsed), catchError(rethrowAsRefusal));
  }
}
